import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiUser, FiMail, FiPhone, FiPackage, FiCalendar, FiActivity, FiSettings, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usersAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import './UserDetails.css';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { partner } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await usersAPI.getById(id);
      
      if (response.success) {
        setUser(response.user);
      } else {
        setError(response.message || 'Failed to load user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Failed to load user details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = () => {
    navigate(`/users/${id}/edit`);
  };

  const handleChangePlan = () => {
    navigate(`/users/${id}/plan`);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    try {
      setResettingPassword(true);
      const response = await usersAPI.update(id, { password: passwordData.newPassword });
      
      if (response.success) {
        toast.success('Password reset successfully');
        setShowPasswordReset(false);
        setPasswordData({ newPassword: '', confirmPassword: '' });
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusClass = (isActive) => {
    return isActive ? 'active' : 'inactive';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const getPlanName = (plan) => {
    const planNames = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta'];
    return plan !== undefined && plan >= 0 && plan < planNames.length 
      ? `${plan} - ${planNames[plan]}` 
      : 'N/A';
  };

  if (loading) {
    return (
      <div className="user-details-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>User Details</h2>
              <p>Loading user information...</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="loading">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-details-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>User Details</h2>
              <p>Error loading user information</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="error-message">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchUserDetails}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-details-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>User Details</h2>
              <p>User not found</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="error-message">
            <p>User not found</p>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-details-page">
      <div className="headerSection">
        <div className="header-content">
          <div className="header-left">
            <h2>User Details</h2>
            <p>View and manage user information</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={handleEditUser}>
              <FiEdit />
              Edit User
            </button>
            <button className="btn btn-primary" onClick={handleChangePlan}>
              <FiPackage />
              Change Plan
            </button>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="dataArea-content">
        <div className="user-details-container">
          <div className="user-header">
            <div className="user-avatar">
              <FiUser />
            </div>
            <div className="user-info">
              <h3>{user.name || 'N/A'}</h3>
              <p className="user-email">{user.email}</p>
              <span className={`status-badge ${getStatusClass(user.isActive)}`}>
                {getStatusText(user.isActive)}
              </span>
            </div>
          </div>

          <div className="user-details-grid">
            <div className="detail-section">
              <h4>Basic Information</h4>
              <div className="detail-item">
                <label>Name:</label>
                <span>{user.name || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Email:</label>
                <span>{user.email}</span>
              </div>
              <div className="detail-item">
                <label>Phone Number:</label>
                <span>{user.phoneNumber || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Company:</label>
                <span>{user.company || 'N/A'}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>Extension Information</h4>
              <div className="detail-item">
                <label>Assigned Extension:</label>
                <span className="extension-badge">
                  {user.extensionId?.name || user.extensionName || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <label>Extension ID:</label>
                <span>{user.extensionId?._id || user.extensionId || 'N/A'}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>Plan Information</h4>
              <div className="detail-item">
                <label>Current Plan:</label>
                <span className="plan-badge">
                  {getPlanName(user.plan)}
                </span>
              </div>
            </div>

            <div className="detail-section">
              <h4>Account Information</h4>
              <div className="detail-item">
                <label>User ID:</label>
                <span>{user._id}</span>
              </div>
              <div className="detail-item">
                <label>Role:</label>
                <span>{user.role || 'user'}</span>
              </div>
              <div className="detail-item">
                <label>Status:</label>
                <span className={`status-badge ${getStatusClass(user.isActive)}`}>
                  {getStatusText(user.isActive)}
                </span>
              </div>
              <div className="detail-item">
                <label>Created At:</label>
                <span>{formatDate(user.date)}</span>
              </div>
              <div className="detail-item">
                <label>Last Login:</label>
                <span>{formatDate(user.lastLogin)}</span>
              </div>
            </div>

            {user.createdBy && (
              <div className="detail-section">
                <h4>Created By</h4>
                <div className="detail-item">
                  <label>Partner:</label>
                  <span>{user.createdBy.name || user.createdBy.firstName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Partner ID:</label>
                  <span>{user.createdBy._id || user.createdBy}</span>
                </div>
              </div>
            )}
          </div>

          {showPasswordReset && (
            <div className="password-reset-section">
              <h4>Reset Password</h4>
              <form onSubmit={handlePasswordReset} className="password-reset-form">
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="password-input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      id="newPassword"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      placeholder="Enter new password (min 6 characters)"
                      required
                      minLength={6}
                      className="form-input"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePasswordVisibility('new')}
                    >
                      {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="password-input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      id="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Confirm new password"
                      required
                      className="form-input"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePasswordVisibility('confirm')}
                    >
                      {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {passwordData.confirmPassword && (
                    <div className={`password-match ${passwordData.newPassword === passwordData.confirmPassword ? 'valid' : 'invalid'}`}>
                      {passwordData.newPassword === passwordData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPasswordReset(false);
                      setPasswordData({ newPassword: '', confirmPassword: '' });
                    }}
                    disabled={resettingPassword}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={resettingPassword}
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="user-actions">
            <button className="btn btn-primary" onClick={handleEditUser}>
              <FiEdit />
              Edit User
            </button>
            <button 
              className={`btn ${showPasswordReset ? 'btn-secondary' : 'btn-warning'}`}
              onClick={() => setShowPasswordReset(!showPasswordReset)}
            >
              <FiLock />
              {showPasswordReset ? 'Cancel Password Reset' : 'Reset Password'}
            </button>
            <button className="btn btn-secondary" onClick={handleChangePlan}>
              <FiPackage />
              Change Plan
            </button>
            <button className="btn btn-danger" onClick={() => navigate('/dashboard')}>
              <FiSettings />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails; 